import React, { useCallback, useEffect, useState } from 'react';
import StarRating from '../components/StarRating';
import ChooseMultiple from '../components/ChooseMultiple';
import type { Question, QuestionOption, ResponseRow } from '../types';
import {
  saveResponse,
  saveResponsesBulk,
  deleteResponsesForQuestion,
  updateSession,
} from '../services/api';

interface SurveyQuestionProps {
  question: Question;
  eventId: number;
  participantId: number;
  questionIndex: number;
  totalQuestions: number;
  existingResponses: ResponseRow[];
  onNext: () => void;
  onSkip: () => void;
  isLast: boolean;
}

export default function SurveyQuestion({
  question,
  eventId,
  participantId,
  questionIndex,
  totalQuestions,
  existingResponses,
  onNext,
  onSkip,
  isLast,
}: SurveyQuestionProps) {
  const [saving, setSaving] = useState(false);
  const [starValue, setStarValue] = useState<number | null>(null);
  const [selectedOptionIds, setSelectedOptionIds] = useState<number[]>([]);
  const [textValue, setTextValue] = useState('');

  useEffect(() => {
    const existing = existingResponses.filter((r) => r.question_id === question.id);
    if (question.question_type === 'STAR_RATING' && existing.length) {
      const v = existing[0].numeric_value;
      setStarValue(v != null ? Number(v) : null);
    } else if (question.question_type === 'STAR_RATING') {
      setStarValue(null);
    }
    if (question.question_type === 'CHOOSE_MULTIPLE' && question.options) {
      const ids = existing.map((r) => r.option_id).filter((id): id is number => id != null);
      setSelectedOptionIds(ids);
    }
    if (question.question_type === 'TEXT') {
      setTextValue(existing.length && existing[0].text_value != null ? String(existing[0].text_value) : '');
    }
  }, [question.id, question.question_type, question.options, existingResponses]);

  const handleNext = useCallback(async () => {
    setSaving(true);
    try {
      if (question.question_type === 'STAR_RATING' && starValue != null) {
        await saveResponse(participantId, eventId, question.id, { numericValue: starValue });
      }
      if (question.question_type === 'CHOOSE_MULTIPLE' && question.options?.length) {
        await deleteResponsesForQuestion(participantId, eventId, question.id);
        const responses = selectedOptionIds.map((optionId) => ({
          questionId: question.id,
          optionId,
        }));
        if (responses.length) await saveResponsesBulk(participantId, eventId, responses);
      }
      if (question.question_type === 'TEXT') {
        await saveResponse(participantId, eventId, question.id, { textValue: textValue.trim() });
      }
      await updateSession(participantId, eventId, {
        lastQuestionId: question.id,
        completedAt: isLast ? new Date().toISOString().slice(0, 19).replace('T', ' ') : undefined,
      });
      onNext();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }, [
    question,
    eventId,
    participantId,
    starValue,
    selectedOptionIds,
    textValue,
    isLast,
    onNext,
  ]);

  const handleSkip = useCallback(async () => {
    setSaving(true);
    try {
      await updateSession(participantId, eventId, { lastQuestionId: question.id });
      onSkip();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }, [participantId, eventId, question.id, onSkip]);

  const min = question.min_value != null ? Number(question.min_value) : 0;
  const max = question.max_value != null ? Number(question.max_value) : 5;
  const step = question.step_value != null ? Number(question.step_value) : 0.25;

  return (
    <div className="page survey-question">
      <div className="survey-question__card">
        <p className="survey-question__progress">
          Question {questionIndex + 1} of {totalQuestions}
        </p>
        <h2 className="survey-question__title">{question.question_text}</h2>

        {question.question_type === 'STAR_RATING' && (
          <div className="survey-question__input">
            <StarRating
              value={starValue}
              onChange={setStarValue}
              min={min}
              max={max}
              step={step}
            />
          </div>
        )}

        {question.question_type === 'CHOOSE_MULTIPLE' && question.options && (
          <div className="survey-question__input">
            <ChooseMultiple
              options={question.options}
              selectedIds={selectedOptionIds}
              onChange={setSelectedOptionIds}
            />
          </div>
        )}

        {question.question_type === 'TEXT' && (
          <div className="survey-question__input">
            <textarea
              className="survey-question__text"
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              placeholder="Your answer (optional)"
              rows={4}
              maxLength={2000}
              aria-label={question.question_text}
            />
          </div>
        )}

        <div className="survey-question__actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleNext}
            disabled={saving}
          >
            {saving ? 'Saving…' : isLast ? 'Finish' : 'Next'}
          </button>
          <button
            type="button"
            className="btn btn--skip"
            onClick={handleSkip}
            disabled={saving}
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
}
